import { apiUrl } from "@workspace/base-api/apiUrl";
import type { ApiError, HttpRequestError } from "@workspace/base-api/fetchJson";
import { fetchJson } from "@workspace/base-api/fetchJson";
import type { RegisteredUser } from "@workspace/profile";
import { toCamel } from "@workspace/shared/utils";

export interface LoginCredentials {
	email: string;
	password: string;
}

export interface CredentialsResult {
	user: RegisteredUser;
	ticket: string;
}

export interface ExchangeResult {
	token: string;
	expires: Date;
	refreshToken: string;
}

export const authApi = {
	async login(credentials: LoginCredentials) {
		try {
			const { user, ticket } = await fetchJson<CredentialsResult>(
				new URL("/auth/credentials", apiUrl),
				{
					method: "POST",
					headers: {},
					body: JSON.stringify(credentials),
				},
			);

			return {
				success: true,
				response: user,
				ticket,
			};
		} catch (e: unknown) {
			const error = e as Partial<HttpRequestError>;
			return {
				success: false,
				details: toCamel(await error.response?.json()) as Record<
					string,
					unknown
				> as ApiError["details"],
				error: error.message || "An error occurred while recording email sent",
			};
		}
	},
	loginOptions: () => {
		return {
			mutationFn: authApi.login,
			mutationKey: ["auth", "login"],
		};
	},

	// Resolves the app user behind an external provider account, PROVED by the subject token the caller already holds
	// (HA-842). The provider's subject is NOT reliably the app user id: Keycloak accounts are created with the app id via
	// partialImport, but that skips accounts already existing under the same email, which keep their own divergent id.
	// The server resolves by the linked account first and falls back to an id match, but only for a token the identity
	// provider vouches for, and answers the same 401 whoever an invalid one names. (This replaced anonymous
	// by-account/by-email lookups that told anyone whether a user exists, with their whole profile.)
	// Returns null when no app user is provisioned for the account, or the token isn't accepted.
	async resolveUser(provider: string, token: string) {
		const url = new URL(`/auth/users/resolve/${provider}`, apiUrl);

		try {
			return await fetchJson<RegisteredUser>(url.toString(), {
				method: "POST",
				headers: {},
				body: JSON.stringify({ subject_token: token }),
			});
		} catch {
			return null;
		}
	},
	async exchangeToken(userId: string, provider: string, token: string) {
		const url = new URL(`auth/token/exchange/${provider}/${userId}`, apiUrl);

		return await fetchJson<ExchangeResult>(url.toString(), {
			method: "POST",
			headers: {},
			body: JSON.stringify({ subject_token: token }),
		});
	},
	async refresh(refreshToken: string) {
		return await fetchJson<ExchangeResult>(
			new URL("/auth/refresh", apiUrl).toString(),
			{
				method: "POST",
				headers: {},
				body: JSON.stringify({ refresh_token: refreshToken }),
			},
		);
	},
	async revokeRefresh(refreshToken: string) {
		try {
			await fetchJson<unknown>(
				new URL("/auth/refresh/revoke", apiUrl).toString(),
				{
					method: "POST",
					headers: {},
					body: JSON.stringify({ refresh_token: refreshToken }),
				},
			);
		} catch {
			return;
		}
	},
	exchangeTokenOptions() {
		return {
			mutationKey: ["auth", "token", "exchange"],
			mutationFn: ({
				userId,
				provider,
				token,
			}: {
				userId: string;
				provider: string;
				token: string;
			}) => authApi.exchangeToken(userId, provider, token),
		};
	},
};
